// ============================================================================
// Dashboard prefs hook — loads / persists the per-user widget layout from
// user_dashboard_prefs (one row per user, RLS own-row).
//
// Semantics:
//   * No row / NULL layout → role default layout (exactly today's dashboard).
//   * First customization upserts the row; subsequent edits update it.
//   * Saves are optimistic — UI applies immediately, persistence runs behind;
//     a failed save surfaces via `saveError` but doesn't revert (the next
//     successful save wins, and a reload re-syncs from the DB).
//   * Reset-to-default sets layout = NULL (block_delete forbids row DELETE).
//
// All role-gate enforcement happens in resolveLayout — a stored key the
// viewer's role can't see is silently dropped.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../shared/supabase';
import { useAuth } from '../../shared/auth';
import type { Json } from '../../types/database.types';
import {
  defaultLayoutForRole,
  resolveLayout,
  type LayoutEntry,
} from './widget-registry';

export interface DashboardPrefs {
  /** False until the initial prefs read settles (renders default meanwhile). */
  ready: boolean;
  layout: LayoutEntry[];
  /** True when the user has a saved customization (enables "Reset"). */
  hasCustom: boolean;
  saveError: string | null;
  /** Optimistically apply + persist a full replacement layout. */
  setLayout: (next: LayoutEntry[]) => void;
  /** Back to the role default; clears the stored layout. */
  reset: () => void;
}

export function useDashboardPrefs(role: string | null): DashboardPrefs {
  const { profile } = useAuth();
  const userId = profile?.id ?? null;

  const [layout, setLayoutState] = useState<LayoutEntry[]>(() =>
    defaultLayoutForRole(role),
  );
  const [ready, setReady] = useState(false);
  const [hasCustom, setHasCustom] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const rowExists = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!userId) {
        setLayoutState(defaultLayoutForRole(role));
        setReady(true);
        return;
      }
      const { data, error } = await supabase
        .from('user_dashboard_prefs')
        .select('layout')
        .eq('user_id', userId)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        // Prefs are a nicety — never block the dashboard on them.
        console.error('[dashboard] prefs load failed', error);
        setLayoutState(defaultLayoutForRole(role));
        setReady(true);
        return;
      }
      rowExists.current = data !== null;
      const stored = data?.layout ?? null;
      setHasCustom(stored !== null);
      setLayoutState(
        stored === null
          ? defaultLayoutForRole(role)
          : resolveLayout(stored, role),
      );
      setReady(true);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [userId, role]);

  const persist = useCallback(
    async (next: LayoutEntry[]) => {
      if (!userId) return;
      setSaveError(null);
      const { error } = await supabase.from('user_dashboard_prefs').upsert(
        {
          user_id: userId,
          // LayoutEntry[] is plain JSON data; the cast bridges to the generated
          // Json column type (TS can't prove index-signature compatibility).
          layout: next as unknown as Json,
        },
        { onConflict: 'user_id' },
      );
      if (error) {
        console.error('[dashboard] prefs save failed', error);
        setSaveError(error.message);
        return;
      }
      rowExists.current = true;
      setHasCustom(true);
    },
    [userId],
  );

  const setLayout = useCallback(
    (next: LayoutEntry[]) => {
      setLayoutState(next);
      void persist(next);
    },
    [persist],
  );

  const reset = useCallback(() => {
    setLayoutState(defaultLayoutForRole(role));
    setHasCustom(false);
    setSaveError(null);
    if (userId && rowExists.current) {
      void supabase
        .from('user_dashboard_prefs')
        .update({ layout: null })
        .eq('user_id', userId)
        .then(({ error }) => {
          if (error) {
            console.error('[dashboard] prefs reset failed', error);
            setSaveError(error.message);
          }
        });
    }
  }, [userId, role]);

  return { ready, layout, hasCustom, saveError, setLayout, reset };
}
